import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import "./GoogleCallback.css";
import getApiUrl from "../utils/api";
import { usePostHog } from "posthog-js/react";

const GoogleCallback = ({ setLoggedIn }) => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);
  const posthog = usePostHog();

  useEffect(() => {
    const success = searchParams.get("success");
    const errorParam = searchParams.get("error");

    if (errorParam) {
      // Show a friendlier message for domain restriction when applicable
      const friendly = /domain is not allowed/i.test(errorParam)
        ? "Your Google account's email domain isn't allowed. Please use your corporate email (limeroad.com, vmart.co.in, vmartretail.com)."
        : "Google authentication failed. Please try again.";
      setError(friendly);
      setLoading(false);
      return;
    }

    if (success) {
      posthog.capture("User Logged In", {
        method: 'google_auth'
      });
      // Fetch user info from /me endpoint
      fetch(getApiUrl("api/auth/me"), {
        credentials: "include",
      })
        .then((res) => res.json())
        .then((data) => {
          if (data.user) {
            const user = data.user;
            posthog.identify(user.userId, { email: user.email, name: `${user.firstName} ${user.lastName}` });
            setLoggedIn(true);
            sessionStorage.setItem("justLoggedIn", "true");
            setTimeout(() => {
              navigate("/mytask");
            }, 1000);
          } else {
            setError("Authentication failed. Please try again.");
            setLoading(false);
          }
        })
        .catch(() => {
          setError("Authentication failed. Please try again.");
          setLoading(false);
        });
    } else {
      setError("Authentication failed. Please try again.");
      setLoading(false);
      posthog.capture('Google Auth Failed', { provider: 'google', reason: errorParam });
    }
  }, [searchParams, navigate, setLoggedIn, posthog]);

  if (loading) {
    return (
      <div className="v-loader-overlay">
        <svg
          className="v-loader-svg v-loader-animate"
          width="120"
          height="120"
          viewBox="0 0 120 120"
        >
          <defs>
            <linearGradient id="v-gradient" x1="0%" y1="0%" x2="100%" y2="100%">
              <stop offset="0%" stopColor="#e30613" stopOpacity="1" />
              <stop offset="50%" stopColor="#ff4757" stopOpacity="1" />
              <stop offset="100%" stopColor="#e30613" stopOpacity="1" />
            </linearGradient>
            <filter id="v-glow" x="-30%" y="-30%" width="160%" height="160%">
              <feDropShadow
                dx="0"
                dy="0"
                stdDeviation="4"
                floodColor="#e30613"
                floodOpacity="0.3"
              />
              <feDropShadow
                dx="0"
                dy="0"
                stdDeviation="8"
                floodColor="#e30613"
                floodOpacity="0.15"
              />
            </filter>
          </defs>
          <g className="v-loader-glow">
            <path
              className="v-loader-path"
              d="M 35 40 L 60 80 L 85 40"
              fill="none"
              strokeWidth="12"
              strokeLinecap="round"
              filter="url(#v-glow)"
            />
          </g>
          {/* Background circle for depth */}
          <circle
            cx="60"
            cy="60"
            r="45"
            fill="none"
            stroke="rgba(227, 6, 19, 0.1)"
            strokeWidth="2"
            strokeDasharray="5,5"
            opacity="0.6"
          />
        </svg>
      </div>
    );
  }

  if (error) {
    return (
      <div className="google-callback-container">
        <div className="google-callback-content">
          <div className="google-callback-error">
            <div className="google-callback-icon">
              <svg
                width="28"
                height="28"
                viewBox="0 0 24 24"
                fill="none"
                aria-hidden
              >
                <circle
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="#ea4335"
                  strokeWidth="2"
                />
                <path
                  d="M15 9l-6 6M9 9l6 6"
                  stroke="#ea4335"
                  strokeWidth="2"
                  strokeLinecap="round"
                />
              </svg>
            </div>
          </div>
          <h2>Authentication Failed</h2>
          <p>
            Sorry for the inconvenience — it looks like your email domain isn’t
            permitted for access. Our records show this account as outside our
            organization. If you believe this is a mistake, please contact your
            administrator.
          </p>
          <p>{error}</p>
          <button
            className="google-callback-retry-btn"
            onClick={() => navigate("/")}
          >
            Back to Login
          </button>
          <div className="google-callback-footer">
            Allowed domains: limeroad.com, vmart.co.in, vmartretail.com
          </div>
        </div>
      </div>
    );
  }

  return null;
};

export default GoogleCallback;
